import { useCallback, useRef } from 'react';
import styles from './QuarterRangeSlider.module.css';

export interface QuarterRangeSliderProps {
  /** Total number of quarters (slider spans indices 0..count-1). */
  count: number;
  /** Current [startIndex, endIndex] inclusive. */
  value: [number, number];
  onChange: (range: [number, number]) => void;
  /** Optional labels for aria (e.g. ["Q1 2020", …]). */
  labels?: string[];
}

/**
 * Two-handle range scrubber over the quarter axis — the d360 ROT widget's
 * HSlider, rebuilt. Dragging a handle trims the visible quarter window.
 */
export function QuarterRangeSlider({ count, value, onChange, labels }: QuarterRangeSliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef<'start' | 'end' | null>(null);
  const max = Math.max(0, count - 1);

  const pctFor = (i: number) => (max === 0 ? 0 : (i / max) * 100);

  const indexFromClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return 0;
      const rect = track.getBoundingClientRect();
      const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
      return Math.round(ratio * max);
    },
    [max],
  );

  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!dragging.current) return;
      const idx = indexFromClientX(e.clientX);
      if (dragging.current === 'start') {
        onChange([Math.min(idx, value[1]), value[1]]);
      } else {
        onChange([value[0], Math.max(idx, value[0])]);
      }
    },
    [indexFromClientX, onChange, value],
  );

  const stop = useCallback(() => {
    dragging.current = null;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', stop);
  }, [onPointerMove]);

  const startDrag = (handle: 'start' | 'end') => (e: React.PointerEvent) => {
    e.preventDefault();
    dragging.current = handle;
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stop);
  };

  const onKey = (handle: 'start' | 'end') => (e: React.KeyboardEvent) => {
    const delta = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0;
    if (!delta) return;
    e.preventDefault();
    if (handle === 'start') onChange([Math.min(Math.max(0, value[0] + delta), value[1]), value[1]]);
    else onChange([value[0], Math.max(Math.min(max, value[1] + delta), value[0])]);
  };

  return (
    <div className={styles.slider} data-print-hide>
      <div ref={trackRef} className={styles.track}>
        <div
          className={styles.range}
          style={{ left: `${pctFor(value[0])}%`, right: `${100 - pctFor(value[1])}%` }}
        />
        <button
          type="button"
          className={styles.handle}
          style={{ left: `${pctFor(value[0])}%` }}
          onPointerDown={startDrag('start')}
          onKeyDown={onKey('start')}
          role="slider"
          aria-label="Range start"
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={value[0]}
          aria-valuetext={labels?.[value[0]]}
        />
        <button
          type="button"
          className={styles.handle}
          style={{ left: `${pctFor(value[1])}%` }}
          onPointerDown={startDrag('end')}
          onKeyDown={onKey('end')}
          role="slider"
          aria-label="Range end"
          aria-valuemin={0}
          aria-valuemax={max}
          aria-valuenow={value[1]}
          aria-valuetext={labels?.[value[1]]}
        />
      </div>
    </div>
  );
}
